import { Component } from '@theme/component';
import { debounce, onDocumentReady } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();
  #submenuAbortController = new AbortController();
  #scrollLocked = false;
  #previousHtmlPaddingRight = '';
  #previousHtmlScrollbarGutter = '';

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    // Keep submenu open while pointer is inside `.menu-list__submenu-inner`.
    // This prevents accidental close when moving from the top-level item into the submenu.
    const cancelDeactivate = () => this.#debouncedDeactivate.cancel();
    const scheduleDeactivate = () => this.#debouncedDeactivate();

    const submenuInners = Array.from(this.querySelectorAll('.menu-list__submenu-inner'));
    for (const inner of submenuInners) {
      inner.addEventListener('pointerenter', cancelDeactivate, {
        signal: this.#submenuAbortController.signal,
        passive: true,
      });
      inner.addEventListener('pointermove', cancelDeactivate, {
        signal: this.#submenuAbortController.signal,
        passive: true,
      });
      inner.addEventListener('pointerdown', cancelDeactivate, {
        signal: this.#submenuAbortController.signal,
        passive: true,
      });
      inner.addEventListener('wheel', cancelDeactivate, {
        signal: this.#submenuAbortController.signal,
        passive: true,
      });
      inner.addEventListener('scroll', cancelDeactivate, {
        signal: this.#submenuAbortController.signal,
        passive: true,
        capture: true,
      });

      inner.addEventListener('pointerleave', scheduleDeactivate, {
        signal: this.#submenuAbortController.signal,
        passive: true,
      });
    }

    onDocumentReady(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#submenuAbortController.abort();
    this.#unlockScroll();
  }

  #lockScroll() {
    if (this.#scrollLocked) return;
    this.#scrollLocked = true;
    const root = document.documentElement;

    // Compensate for scrollbar disappearance to prevent layout shift (horizontal "stretching").
    const scrollbarWidth = window.innerWidth - root.clientWidth;
    this.#previousHtmlPaddingRight = root.style.paddingRight;
    this.#previousHtmlScrollbarGutter = root.style.scrollbarGutter;
    if (scrollbarWidth > 0) root.style.paddingRight = `${scrollbarWidth}px`;
    // Helps in browsers that support it.
    root.style.scrollbarGutter = 'stable';

    root.setAttribute('data-header-menu-open', '');
  }

  #unlockScroll() {
    if (!this.#scrollLocked) return;
    this.#scrollLocked = false;
    const root = document.documentElement;
    root.removeAttribute('data-header-menu-open');

    root.style.paddingRight = this.#previousHtmlPaddingRight;
    root.style.scrollbarGutter = this.#previousHtmlScrollbarGutter;
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    this.#lockScroll();

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // If pointer/focus is moving into the submenu content, don't start closing.
    // This is especially important when leaving the top-level menu item and entering `.menu-list__submenu-inner`.
    const relatedTarget = /** @type {any} */ (event).relatedTarget;
    if (item && relatedTarget instanceof Element) {
      const submenuInner = item.parentElement?.querySelector('[ref="submenu[]"] .menu-list__submenu-inner');
      if (submenuInner instanceof HTMLElement && submenuInner.contains(relatedTarget)) return;
    }

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    this.#unlockScroll();
    item.setAttribute('data-animating', '');

    setTimeout(
      () => {
        item.removeAttribute('data-animating');
      },
      Math.max(0, this.animationDelay - 150)
    ); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
